import type { IAnyAction } from './baseTypes';
import type { Joi, ExtractValues } from '../helpers';

type ChannelFactory<Args extends unknown[]> =
  | ((...payload: Args) => string | undefined)
  | (() => string | undefined);

type ArgsForAction<E extends IAnyAction> = E['payload'] extends
  | object
  | string
  | number
  ? [E['payload']]
  : [];

export interface IActionCreator<
  E extends IAnyAction = IAnyAction,
  Args extends unknown[] = ArgsForAction<E>
> {
  (...params: Args): E;
  readonly type: E['type'];
  readonly schema: Joi.ObjectSchema<E>;
  readonly channels?: Array<ChannelFactory<Args>>;
}

export interface ITopicBoundActionCreator<
  E extends IAnyAction = IAnyAction,
  T extends string = string,
  Args extends unknown[] = ArgsForAction<E>
> extends IActionCreator<E, Args> {
  readonly topic: T;
}

export interface IActionCreatorParam<
  E extends IAnyAction = IAnyAction,
  Args extends unknown[] = ArgsForAction<E>
> {
  type: E['type'];
  schema: Joi.ObjectSchema<E>;
  channels?: Array<ChannelFactory<Args>>;
}

export interface ICustomActionCreatorParam<
  E extends IAnyAction = IAnyAction,
  Args extends unknown[] = ArgsForAction<E>
> {
  type: E['type'];
  schema: Joi.ObjectSchema<E>;
  creator: (...payload: Args) => E;
  channels?: Array<ChannelFactory<Args>>;
}

export interface ITopicBoundActionCreatorParam<
  E extends IAnyAction = IAnyAction,
  T extends string = string,
  Args extends unknown[] = ArgsForAction<E>
> extends IActionCreatorParam<E, Args> {
  topic: T;
}

export interface ICustomTopicBoundActionCreatorParam<
  E extends IAnyAction = IAnyAction,
  T extends string = string,
  Args extends unknown[] = ArgsForAction<E>
> extends ICustomActionCreatorParam<E, Args> {
  topic: T;
}

export interface IActionCreatorFactory {
  <E extends IAnyAction, T extends string>(
    param: ITopicBoundActionCreatorParam<E, T>
  ): ITopicBoundActionCreator<E, T>;

  <E extends IAnyAction, T extends string, Args extends unknown[]>(
    param: ICustomTopicBoundActionCreatorParam<E, T, Args>
  ): ITopicBoundActionCreator<
    ReturnType<typeof param['creator']>,
    typeof param['topic'],
    Parameters<typeof param['creator']>
  >;

  <E extends IAnyAction>(param: IActionCreatorParam<E>): IActionCreator<E>;

  <E extends IAnyAction, Args extends unknown[]>(
    param: ICustomActionCreatorParam<E, Args>
  ): IActionCreator<
    ReturnType<typeof param['creator']>,
    Parameters<typeof param['creator']>
  >;
}

type AnyParam<
  E extends IAnyAction = IAnyAction,
  Args extends unknown[] = ArgsForAction<E>,
  T extends string = string
> =
  | IActionCreatorParam<E, Args>
  | ICustomActionCreatorParam<E, Args>
  | ITopicBoundActionCreatorParam<E, T, Args>
  | ICustomTopicBoundActionCreatorParam<E, T, Args>;

export const actionCreator: IActionCreatorFactory = <
  E extends IAnyAction,
  Args extends unknown[]
>(
  param: AnyParam<E, Args>
) => {
  const defaultActionCreator = (payload: E['payload']) =>
    ({
      type: param.type,
      ...(payload && {
        payload,
      }),
    } as E);
  const creator = (('creator' in param && param.creator) ||
    defaultActionCreator) as {
    (...payload: Args): E;
    type: string;
    schema: Joi.ObjectSchema<E>;
    topic: string;
    channels?: Array<ChannelFactory<Args>>;
  };
  creator.type = param.type;
  creator.schema = param.schema;

  if ('topic' in param) {
    creator.topic = param.topic;
  }
  if ('channels' in param) {
    creator.channels = param.channels;
  }

  return Object.freeze(creator);
};

export type ActionsOfCreators<
  A extends IActionCreator<IAnyAction, unknown[]>[]
> = ReturnType<ExtractValues<A>>;
